import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "./ui/dialog";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldAlert, MessageCircle } from "lucide-react";
import { submitEnquiry, waLink } from "../lib/api";

const LOAN_TYPES = [
  "Personal Loan", "Business Loan", "Home Loan", "Loan Against Property",
  "Vehicle Loan", "Loan Balance Transfer", "Health Insurance", "Life Insurance",
  "Motor Insurance", "Credit Score & Report", "Other",
];

export function LeadForm({ defaultType = "", onDone, compact = false }) {
  const [form, setForm] = useState({
    name: "", mobile: "", email: "", city: "", type: defaultType, message: "",
  });
  const [consent, setConsent] = useState(false); // never pre-ticked
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile) {
      toast.error("Please enter your name and mobile number.");
      return;
    }
    if (!/^[6-9]\d{9}$/.test(form.mobile)) {
      toast.error("Please enter a valid 10-digit Indian mobile number.");
      return;
    }
    if (!consent) {
      toast.error("Please provide your consent to proceed.");
      return;
    }
    setSubmitting(true);
    try {
      await submitEnquiry({ ...form, consent: true, source: "website" });
      toast.success("Enquiry submitted. Our team will get in touch. This does not guarantee loan approval.");
      setForm({ name: "", mobile: "", email: "", city: "", type: defaultType, message: "" });
      setConsent(false);
      onDone && onDone();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  const sendWhatsApp = () => {
    if (!form.name || !form.mobile) {
      toast.error("Please enter your name and mobile number first.");
      return;
    }
    const msg = `Hi Loansupportindia, I would like to enquire.\n\nName: ${form.name}\nMobile: ${form.mobile}` +
      (form.email ? `\nEmail: ${form.email}` : "") +
      (form.city ? `\nCity: ${form.city}` : "") +
      (form.type ? `\nRequirement: ${form.type}` : "") +
      (form.message ? `\nMessage: ${form.message}` : "");
    window.open(waLink(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className={compact ? "space-y-4" : "grid sm:grid-cols-2 gap-4"}>
        <div className="space-y-1.5">
          <Label htmlFor="lf-name">Full Name *</Label>
          <Input id="lf-name" value={form.name} onChange={set("name")} placeholder="Your name" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lf-mobile">Mobile Number *</Label>
          <Input id="lf-mobile" value={form.mobile} onChange={set("mobile")} placeholder="10-digit mobile" maxLength={10} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lf-email">Email</Label>
          <Input id="lf-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="lf-city">City</Label>
          <Input id="lf-city" value={form.city} onChange={set("city")} placeholder="Your city" />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Requirement</Label>
          <Select value={form.type} onValueChange={set("type")}>
            <SelectTrigger><SelectValue placeholder="Select a product" /></SelectTrigger>
            <SelectContent>
              {LOAN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="lf-msg">Message (optional)</Label>
          <Textarea id="lf-msg" value={form.message} onChange={set("message")} placeholder="Tell us briefly about your requirement" rows={3} />
        </div>
      </div>

      <div className="flex items-start gap-3 rounded-lg bg-secondary/60 p-3">
        <Checkbox id="lf-consent" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5" />
        <Label htmlFor="lf-consent" className="text-xs leading-relaxed text-muted-foreground font-normal cursor-pointer">
          I agree to Loansupportindia and its authorised partners/lenders contacting me regarding loan/financial
          products and processing my enquiry in accordance with the{" "}
          <Link to="/privacy-policy" className="text-primary underline">Privacy Policy</Link>,{" "}
          <Link to="/terms" className="text-primary underline">Terms &amp; Conditions</Link> and{" "}
          <Link to="/disclaimer" className="text-primary underline">Disclaimer</Link>. I understand that submission of
          this form does not guarantee loan approval.
        </Label>
      </div>

      <Button type="submit" disabled={submitting} className="w-full h-11 text-base">
        {submitting ? "Submitting..." : "Submit Enquiry"}
      </Button>

      <button type="button" onClick={sendWhatsApp}
        className="flex w-full items-center justify-center gap-2 rounded-md border border-[#25D366] bg-[#25D366]/10 py-2.5 text-sm font-semibold text-[#128C4A] transition-colors hover:bg-[#25D366]/20">
        <MessageCircle className="h-4 w-4" /> Send on WhatsApp instead
      </button>

      <p className="flex items-start gap-2 text-[11px] text-muted-foreground">
        <ShieldAlert className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
        Loan approval, interest rate, tenure and other terms are subject to the respective lender's eligibility criteria
        and underwriting policies.
      </p>
    </form>
  );
}

export function EnquiryDialog({ trigger, defaultType = "" }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Submit an Enquiry</DialogTitle>
          <DialogDescription>
            Share a few details and our team will assist you. Fields marked * are required.
          </DialogDescription>
        </DialogHeader>
        <LeadForm defaultType={defaultType} onDone={() => setOpen(false)} />
      </DialogContent>
    </Dialog>
  );
}
